import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import { objectKeys } from "app/helpers/objects";
import {
    resetFilteringStateAction,
    updateIsNewOnlyAction,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import { setLeadsFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters/store/action-creators";
import { appliedSignalsChangedAction } from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";

import { DEFAULT_ORDER_ASC, DEFAULT_PAGE, ORDER_BY_MAP, TERRITORY_ORDER_BY } from "../constants/dto";
import { isSameExpandedRowVariant } from "../utils/expanded-row";

import * as ac from "./action-creators";
import initialLeadsSearchState, {
    createInitialExpandedRowState,
    getExpandedRowStateByVariant,
    resetTabsPaginationState,
} from "./initial-state";
import type { LeadsSearchState } from "./types";

type FilterChangeActions =
    | typeof updateIsNewOnlyAction
    | typeof setLeadsFilterValue
    | typeof appliedSignalsChangedAction;

const leadsSearchReducer = createReducer<
    LeadsSearchState,
    ActionType<typeof ac | FilterChangeActions> | ReturnType<typeof resetFilteringStateAction>
>(initialLeadsSearchState)
    .handleAction(
        ac.setSelectedRows,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            selectedRows: payload,
        }),
    )
    .handleAction(
        ac.setIsRowSelectionEnabled,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            isRowSelectionEnabled: payload,
        }),
    )
    .handleAction(
        ac.setTableRequestParams,
        (state, { payload: { leadsType, ...params } }): LeadsSearchState => ({
            ...state,
            ...createInitialExpandedRowState(),
            tabs: {
                ...state.tabs,
                [leadsType]: {
                    ...state.tabs[leadsType],
                    ...params,
                },
            },
        }),
    )
    .handleAction(
        ac.applyTerritoryTableSort,
        (state, { payload: isTerritorySort }): LeadsSearchState => ({
            ...state,
            ...createInitialExpandedRowState(),
            tabs: objectKeys(state.tabs).reduce<LeadsSearchState["tabs"]>(
                (tabs, id) => {
                    tabs[id] = {
                        ...state.tabs[id],
                        orderBy: isTerritorySort ? TERRITORY_ORDER_BY : ORDER_BY_MAP[id],
                        asc: DEFAULT_ORDER_ASC,
                        page: DEFAULT_PAGE,
                    };

                    return tabs;
                },
                {} as LeadsSearchState["tabs"],
            ),
        }),
    )
    .handleAction(
        [ac.resetTabsPagination, updateIsNewOnlyAction, setLeadsFilterValue, appliedSignalsChangedAction],
        (state): LeadsSearchState => ({
            ...state,
            ...createInitialExpandedRowState(),
            tabs: resetTabsPaginationState(state.tabs),
        }),
    )
    .handleAction(
        ac.setUseAggregatedCountryData,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            ...createInitialExpandedRowState(),
            isAggregatedDataUsed: payload,
        }),
    )
    .handleAction(
        ac.setSearchText,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            ...createInitialExpandedRowState(),
            searchText: payload,
            tabs: resetTabsPaginationState(state.tabs),
        }),
    )
    .handleAction(
        ac.resetSearchText,
        (state): LeadsSearchState => ({
            ...state,
            ...createInitialExpandedRowState(),
            searchText: initialLeadsSearchState.searchText,
        }),
    )
    .handleAction(
        ac.setActiveLeadsType,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            ...createInitialExpandedRowState(),
            activeLeadsType: payload,
        }),
    )
    .handleAction(
        ac.setExpandedRowSort,
        (state, { payload: { columnKey, order } }): LeadsSearchState => ({
            ...state,
            expandedRowState: {
                ...state.expandedRowState,
                orderBy: columnKey,
                asc: order === "ascend",
                page: DEFAULT_PAGE,
            },
        }),
    )
    .handleAction(
        ac.setExpandedRowPage,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            expandedRowState: {
                ...state.expandedRowState,
                page: payload,
            },
        }),
    )
    .handleAction(ac.toggleExpandedRow, (state, { payload: { key, variant } }): LeadsSearchState => {
        const isAlreadyOpen =
            state.expandedRowKeys.includes(key) && isSameExpandedRowVariant(state.expandedRowVariant, variant);

        if (isAlreadyOpen) {
            return { ...state, ...createInitialExpandedRowState() };
        }

        return {
            ...state,
            expandedRowKeys: [key],
            expandedRowVariant: variant,
            expandedRowState: getExpandedRowStateByVariant(variant),
        };
    })
    .handleAction(
        ac.setColumnNamesForHighlight,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            columnNamesForHighlight: payload,
        }),
    )
    .handleAction(
        ac.setHighlightedRowDomains,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            highlightedRowDomains: payload,
        }),
    )
    .handleAction(
        ac.setIsOpenListPanel,
        (state, { payload }): LeadsSearchState => ({
            ...state,
            isShowSavedSearchListPanel: payload,
        }),
    )
    .handleAction(resetFilteringStateAction, (): LeadsSearchState => initialLeadsSearchState);

export default leadsSearchReducer;
